import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Typography from "@material-ui/core/Typography";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  helpText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
}));

const VersionSchema = Yup.object().shape({
  version: Yup.string()
    .matches(
      /^\d+\.\d+\.\d+(-alpha|-beta)?$/,
      "Formato invalido. Use: X.XXXX.XXXXXXXXXX (ex: 2.3000.1024710243)"
    )
    .required("Obrigatorio"),
});

const WhatsappVersionModal = ({ open, onClose, versionId }) => {
  const classes = useStyles();

  const initialState = {
    version: "",
    isDefault: false,
  };

  const [version, setVersion] = useState(initialState);

  useEffect(() => {
    const fetchVersion = async () => {
      if (!versionId) return;
      try {
        const { data } = await api.get(`/whatsapp-versions/${versionId}`);
        setVersion({
          version: data.version,
          isDefault: data.isDefault,
        });
      } catch (err) {
        toastError(err);
      }
    };

    if (open && versionId) {
      fetchVersion();
    } else {
      setVersion(initialState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId, open]);

  const handleClose = () => {
    setVersion(initialState);
    onClose();
  };

  const handleSaveVersion = async (values) => {
    try {
      if (versionId) {
        await api.put(`/whatsapp-versions/${versionId}`, values);
        toast.success(i18n.t("whatsappVersions.toasts.updated"));
      } else {
        await api.post("/whatsapp-versions", values);
        toast.success(i18n.t("whatsappVersions.toasts.created"));
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {versionId
            ? i18n.t("whatsappVersionModal.title.edit")
            : i18n.t("whatsappVersionModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={version}
          enableReinitialize={true}
          validationSchema={VersionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveVersion(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("whatsappVersionModal.form.version")}
                  name="version"
                  error={touched.version && Boolean(errors.version)}
                  helperText={touched.version && errors.version}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  disabled={!!versionId}
                  placeholder="2.3000.1024710243"
                />
                <Typography className={classes.helpText}>
                  {i18n.t("whatsappVersionModal.form.versionHelp")}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isDefault}
                      onChange={(e) =>
                        setFieldValue("isDefault", e.target.checked)
                      }
                      name="isDefault"
                      color="primary"
                    />
                  }
                  label={i18n.t("whatsappVersionModal.form.setAsDefault")}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("whatsappVersionModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {versionId
                    ? i18n.t("whatsappVersionModal.buttons.okEdit")
                    : i18n.t("whatsappVersionModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default WhatsappVersionModal;
